import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const BASE_URL = config.BASE_URL;

const STATUS_OPTIONS = [
  { value: 'Acknowledged', label: '🟡 Acknowledged', color: '#d97706' },
  { value: 'In Progress', label: '🔵 In Progress', color: '#2563eb' },
  { value: 'Resolved', label: '✅ Resolved', color: '#16a34a' },
  { value: 'Accepted Risk', label: '⚫ Accepted Risk', color: '#6b7280' },
  { value: 'Suppressed', label: '🫥 Suppressed', color: '#7c3aed' },
];

const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
const VERIFICATION_OPTIONS = ['Pending', 'Verified', 'Failed', 'Not Needed'];
const TERMINAL_STATUSES = new Set(['Resolved', 'Accepted Risk', 'Suppressed']);
const SEVERITY_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#16a34a' };

function statusBadge(status) {
  if (!status) return null;
  const cls = {
    'Acknowledged': 'open',
    'In Progress': 'in-progress',
    'Resolved': 'resolved',
    'Accepted Risk': 'mitigated',
    'Suppressed': 'false-pos',
  }[status] || 'open';
  return <span className={`ce-status ce-status--${cls}`}>{status}</span>;
}

function verificationBadge(status) {
  if (!status) return null;
  const colors = {
    Pending: '#f59e0b',
    Verified: '#16a34a',
    Failed: '#dc2626',
    'Not Needed': '#64748b',
  };
  const color = colors[status] || '#64748b';
  return (
    <span style={{
      background: `${color}18`,
      color,
      border: `1px solid ${color}33`,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '0.72rem',
      fontWeight: 600,
    }}>
      {status === 'Verified' ? '✔ Verified' : `Verify: ${status}`}
    </span>
  );
}

function isOverdue(item) {
  if (!item?.dueDate || TERMINAL_STATUSES.has(item.status)) return false;
  return new Date(item.dueDate) < new Date();
}

function normalizePriority(priority, fallbackSeverity) {
  if (priority) return priority;
  const normalizedSeverity = String(fallbackSeverity || '').trim().toLowerCase();
  if (!normalizedSeverity) return '';
  return normalizedSeverity.charAt(0).toUpperCase() + normalizedSeverity.slice(1);
}

function newForm(existing, vuln) {
  return existing ? {
    status: existing.status || 'Acknowledged',
    priority: existing.priority || '',
    owner: existing.owner || '',
    dueDate: existing.dueDate || '',
    notes: existing.notes || '',
    suppressionReason: existing.suppressionReason || '',
    exceptionReason: existing.exceptionReason || '',
    verificationStatus: existing.verificationStatus || 'Pending',
    verificationNotes: existing.verificationNotes || '',
    ticketRef: existing.ticketRef || '',
  } : {
    status: 'Acknowledged',
    priority: normalizePriority('', vuln?.severity),
    owner: '',
    dueDate: '',
    notes: '',
    suppressionReason: '',
    exceptionReason: '',
    verificationStatus: 'Pending',
    verificationNotes: '',
    ticketRef: '',
  };
}

export default function RemediationView({ repoName, commitHash }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vulns, setVulns] = useState([]);
  const [remMap, setRemMap] = useState({});
  const [historyMap, setHistoryMap] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState('');
  const [editError, setEditError] = useState('');
  const [actionFeedback, setActionFeedback] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newForm(null, null));
  const [saving, setSaving] = useState(false);
  const [jiraIntegration, setJiraIntegration] = useState({ enabled: false, projectKey: '', issueType: '' });
  const [ticketLoading, setTicketLoading] = useState({});
  const [githubIntegration, setGithubIntegration] = useState({ enabled: false, defaultOwner: '', defaultRepo: '' });
  const [githubTicketLoading, setGithubTicketLoading] = useState({});

  const filter = searchParams.get('rem_filter') || 'all';
  const page = Math.max(1, parseInt(searchParams.get('rem_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const setFilter = (val) => setSearchParams(prev => {
    const n = new URLSearchParams(prev);
    n.set('rem_filter', val);
    n.set('rem_page', '1');
    return n;
  });
  const setPage = (val) => setSearchParams(prev => {
    const n = new URLSearchParams(prev);
    n.set('rem_page', String(val));
    return n;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarning('');
    try {
      const sbomFile = commitHash.endsWith('-sbom.json') ? commitHash : `${commitHash}-sbom.json`;
      const [vRes, rRes] = await Promise.allSettled([
        authFetch(`${BASE_URL}/sbom/${repoName}/${sbomFile}/vulnerabilities`),
        authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/remediation`),
      ]);
      if (vRes.status !== 'fulfilled' || !vRes.value.ok) {
        const status = vRes.status === 'fulfilled' ? vRes.value.status : null;
        throw new Error(status ? `HTTP ${status}` : (vRes.reason?.message || t('remediation.loadError')));
      }

      const vData = await vRes.value.json();
      let rData = [];
      if (rRes.status === 'fulfilled') {
        if (rRes.value.ok) {
          rData = await rRes.value.json();
        } else if (rRes.value.status !== 404) {
          setWarning(t('remediation.partialWarning'));
        }
      } else {
        setWarning(t('remediation.partialWarning'));
      }

      const vulnList = Array.isArray(vData) ? vData : (vData?.vulnerabilities || []);
      setVulns(vulnList);
      const map = {};
      (Array.isArray(rData) ? rData : []).forEach((r) => { map[r.vulnId] = r; });
      setRemMap(map);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [repoName, commitHash, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    authFetch(`${BASE_URL}/integrations/jira/status`)
      .then(async (response) => {
        if (!response.ok) return { enabled: false, projectKey: '', issueType: '' };
        return response.json();
      })
      .then((data) => setJiraIntegration(data || { enabled: false, projectKey: '', issueType: '' }))
      .catch(() => setJiraIntegration({ enabled: false, projectKey: '', issueType: '' }));

    authFetch(`${BASE_URL}/integrations/github/status`)
      .then(r => r.json())
      .then(data => setGithubIntegration(data || { enabled: false, defaultOwner: '', defaultRepo: '' }))
      .catch(() => setGithubIntegration({ enabled: false, defaultOwner: '', defaultRepo: '' }));
  }, []);

  const loadHistory = useCallback(async (vulnId) => {
    setHistoryLoading(prev => ({ ...prev, [vulnId]: true }));
    try {
      const response = await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}/history`);
      if (!response.ok) {
        throw new Error('Failed to load history');
      }
      const data = await response.json();
      setHistoryMap(prev => ({ ...prev, [vulnId]: Array.isArray(data) ? data : [] }));
    } catch (_) {
      setHistoryMap(prev => ({ ...prev, [vulnId]: [] }));
    } finally {
      setHistoryLoading(prev => ({ ...prev, [vulnId]: false }));
    }
  }, [repoName, commitHash]);

  const startEdit = (vuln) => {
    const vulnId = vuln.vulnId || vuln.id;
    const existing = remMap[vulnId];
    setEditError('');
    setActionFeedback(null);
    setForm(newForm(existing, vuln));
    setEditing(vulnId);
    if (existing) {
      loadHistory(vulnId);
    }
  };

  const save = async () => {
    setSaving(true);
    setEditError('');
    setActionFeedback(null);
    try {
      const response = await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(editing)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || t('remediation.saveError', 'Failed to save remediation item.'));
      }
      setEditing(null);
      await load();
      await loadHistory(editing);
    } catch (e) {
      setEditError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (vulnId) => {
    if (!window.confirm(t('remediation.confirmDelete', 'Remove remediation tracking for this CVE?'))) return;
    await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}`, { method: 'DELETE' });
    setHistoryMap(prev => ({ ...prev, [vulnId]: [] }));
    await load();
  };

  const runJiraAction = async (vuln, action) => {
    const vulnId = vuln.vulnId || vuln.id;
    setTicketLoading((prev) => ({ ...prev, [vulnId]: action }));
    setEditError('');
    setActionFeedback(null);
    try {
      const endpoint = action === 'create'
        ? `${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}/jira`
        : `${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}/jira/sync`;
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageName: vuln.packageName || vuln.package_name || '',
          version: vuln.version || '',
          severity: vuln.severity || '',
          summary: vuln.summary || '',
          fixedIn: vuln.fixedIn || vuln.fixed_in || '',
          owner: remMap[vulnId]?.owner || '',
          priority: remMap[vulnId]?.priority || '',
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Jira action failed.');
      }
      const updated = await response.json();
      setRemMap((prev) => ({ ...prev, [vulnId]: updated }));
      await loadHistory(vulnId);
      await load();
      setActionFeedback({
        type: 'success',
        message: action === 'create'
          ? `Created Jira issue ${updated?.ticketRef || ''}`.trim()
          : `Synced Jira status${updated?.ticketStatus ? `: ${updated.ticketStatus}` : ''}`,
      });
    } catch (e) {
      setEditError(e.message);
      setActionFeedback({ type: 'danger', message: e.message || 'Jira action failed.' });
    } finally {
      setTicketLoading((prev) => {
        const next = { ...prev };
        delete next[vulnId];
        return next;
      });
    }
  };

  const runGitHubAction = async (vuln, action) => {
    const vulnId = vuln.id || vuln.vulnId || (vuln.aliases && vuln.aliases[0]) || '';
    setGithubTicketLoading(prev => ({ ...prev, [vulnId]: action }));
    setActionFeedback(null);
    try {
      const rem = remMap[vulnId];
      const url = action === 'create'
        ? `${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}/github`
        : `${BASE_URL}/sbom/${repoName}/${commitHash}/remediation/${encodeURIComponent(vulnId)}/github/sync`;
      const body = action === 'create' ? JSON.stringify({
        packageName: vuln.packageName || vuln.package_name || vuln.package || '',
        version: vuln.version || '',
        severity: vuln.severity || '',
        fixedIn: vuln.fixedIn || vuln.fixed_in || '',
        summary: rem?.notes || '',
      }) : JSON.stringify({});
      const resp = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || 'GitHub action failed.');
      setRemMap(prev => ({ ...prev, [vulnId]: { ...prev[vulnId], ...data } }));
      await loadHistory(vulnId);
      await load();
      setActionFeedback({
        type: 'success',
        message: action === 'create'
          ? `Created GitHub Issue ${data?.ticketRef || ''}`.trim()
          : `Synced GitHub status${data?.ticketStatus ? `: ${data.ticketStatus}` : ''}`,
      });
    } catch (e) {
      setActionFeedback({ type: 'danger', message: e.message || 'GitHub action failed.' });
    } finally {
      setGithubTicketLoading(prev => ({ ...prev, [vulnId]: null }));
    }
  };

  const overdueItems = Object.values(remMap).filter(isOverdue);
  const unassignedCritical = Object.values(remMap).filter(r => r.priority === 'Critical' && !r.owner && !TERMINAL_STATUSES.has(r.status)).length;
  const stats = {
    total: vulns.length,
    tracked: Object.keys(remMap).length,
    inProgress: Object.values(remMap).filter(r => r.status === 'In Progress').length,
    resolved: Object.values(remMap).filter(r => r.status === 'Resolved').length,
    acceptedRisk: Object.values(remMap).filter(r => r.status === 'Accepted Risk').length,
    suppressed: Object.values(remMap).filter(r => r.status === 'Suppressed').length,
    verified: Object.values(remMap).filter(r => r.verificationStatus === 'Verified').length,
    overdue: overdueItems.length,
    unassignedCritical,
  };

  const filtered = vulns.filter((v) => {
    const vulnId = v.vulnId || v.id;
    const rem = remMap[vulnId];
    if (filter === 'acknowledged') return rem?.status === 'Acknowledged';
    if (filter === 'inprogress') return rem?.status === 'In Progress';
    if (filter === 'resolved') return rem?.status === 'Resolved';
    if (filter === 'accepted') return rem?.status === 'Accepted Risk';
    if (filter === 'suppressed') return rem?.status === 'Suppressed';
    if (filter === 'verified') return rem?.verificationStatus === 'Verified';
    if (filter === 'overdue') return rem && isOverdue(rem);
    if (filter === 'untracked') return !rem;
    return true;
  });

  const nPages = Math.ceil(filtered.length / pageSize);
  const pagedFiltered = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (loading) {
    return (
      <div style={{ padding: '1rem 0' }}>
        <Skeleton.Table rows={5} cols={5} />
      </div>
    );
  }
  if (error) return <div className="alert alert-danger">{t('remediation.loadError')}: {error}</div>;

  return (
    <div>
      {warning && <div className="alert alert-warning">{warning}</div>}
      {actionFeedback && (
        <div className={`alert alert-${actionFeedback.type === 'success' ? 'success' : 'danger'}`}>
          {actionFeedback.message}
        </div>
      )}

      <div className="ce-kpi-row" style={{ marginBottom: '1.25rem' }}>
        {[
          { label: t('remediation.totalCves', 'Total CVEs'), value: stats.total, color: '#6366f1' },
          { label: t('remediation.tracked', 'Tracked'), value: `${stats.tracked}/${stats.total}`, color: '#0891b2' },
          { label: t('remediation.inProgress', 'In Progress'), value: stats.inProgress, color: '#2563eb' },
          { label: t('remediation.resolved', 'Resolved'), value: stats.resolved, color: stats.resolved > 0 ? '#059669' : '#6b7280' },
          { label: t('remediation.verified', 'Verified'), value: stats.verified, color: stats.verified > 0 ? '#16a34a' : '#6b7280' },
          { label: t('remediation.acceptedRisk', 'Accepted Risk'), value: stats.acceptedRisk, color: '#6b7280' },
          { label: t('remediation.suppressed', 'Suppressed'), value: stats.suppressed, color: '#7c3aed' },
          { label: t('remediation.overdue', 'Overdue'), value: stats.overdue, color: stats.overdue > 0 ? '#dc2626' : '#6b7280' },
          { label: t('remediation.unassignedCritical', 'Unassigned Critical'), value: stats.unassignedCritical, color: stats.unassignedCritical > 0 ? '#dc2626' : '#6b7280' },
        ].map((s) => (
          <div key={s.label} className="ce-kpi" style={{ '--kpi-color': s.color }}>
            <div className="ce-kpi-value">{s.value}</div>
            <div className="ce-kpi-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="ce-filter-bar">
        <span style={{ fontSize: '0.8rem', color: 'var(--bs-secondary-color)' }}>{t('remediation.filter', 'Filter:')}</span>
        {[
          { key: 'all', label: t('remediation.filterAll', 'All') },
          { key: 'untracked', label: t('remediation.filterUntracked', '⬜ Untracked') },
          { key: 'acknowledged', label: t('remediation.filterAcknowledged', '🟡 Acknowledged') },
          { key: 'inprogress', label: t('remediation.filterInProgress', '🔵 In Progress') },
          { key: 'resolved', label: t('remediation.filterResolved', '✅ Resolved') },
          { key: 'verified', label: t('remediation.filterVerified', '✔ Verified') },
          { key: 'accepted', label: t('remediation.filterAcceptedRisk', '⚫ Accepted Risk') },
          { key: 'suppressed', label: t('remediation.filterSuppressed', '🫥 Suppressed') },
          { key: 'overdue', label: t('remediation.filterOverdue', '⏰ Overdue') },
        ].map((f) => (
          <button
            key={f.key}
            className={`ce-btn ce-btn--sm ${filter === f.key ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="ce-filter-count">{filtered.length} {t('remediation.results', 'results')}</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="📋"
          title={t('remediation.emptyTitle')}
          description={t('remediation.emptyDescription')}
          size="sm"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {pagedFiltered.map((v) => {
            const vulnId = v.vulnId || v.id;
            const rem = remMap[vulnId];
            const isEditing = editing === vulnId;
            const pkgName = v.packageName || v.package_name || '';
            const cvssScore = v.cvssScore ?? v.cvss_score ?? 0;
            const overdue = rem && isOverdue(rem);
            const history = historyMap[vulnId] || [];

            return (
              <div key={vulnId} className="ce-card" style={{ marginBottom: '0.5rem', borderColor: overdue ? '#dc2626' : undefined }}>
                <div className="ce-card-body" style={{ padding: '0.875rem 1rem' }}>
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <code style={{ fontSize: '0.8125rem', fontWeight: 700 }}>{vulnId}</code>
                      <span className={`ce-sev ce-sev--${(v.severity || 'none').toLowerCase()}`}>{v.severity || '?'}</span>
                      {cvssScore > 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>CVSS {cvssScore.toFixed(1)}</span>
                      )}
                      {rem && statusBadge(rem.status)}
                      {rem?.verificationStatus && verificationBadge(rem.verificationStatus)}
                      {overdue && (
                        <span className="ce-sev ce-sev--critical">
                          {t('remediation.overdueLabel', '⏰ OVERDUE')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>
                      {pkgName}{v.version ? `@${v.version}` : ''}
                      {(v.fixedIn || v.fixed_in) && (
                        <span className="ce-sev ce-sev--low" style={{ marginLeft: '0.5rem' }}>🔧 → {v.fixedIn || v.fixed_in}</span>
                      )}
                    </div>
                    {v.summary && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--bs-body-color)', lineHeight: 1.5 }}>{v.summary}</div>
                    )}
                    {rem && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        {rem.owner && <span>👤 {rem.owner}</span>}
                        {rem.dueDate && <span>📅 {rem.dueDate}</span>}
                        {rem.priority && <span>🎯 {rem.priority}</span>}
                        {rem.ticketRef && (
                          rem.ticketUrl ? (
                            <a href={rem.ticketUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                              🎫 {rem.ticketRef}
                            </a>
                          ) : <span>🎫 {rem.ticketRef}</span>
                        )}
                        {rem.ticketStatus && <span>{rem.ticketType === 'github' ? '🐙' : '📌'} {rem.ticketType === 'github' ? 'GitHub' : 'Jira'}: {rem.ticketStatus}</span>}
                        {rem.ticketSyncedAt && <span>🔄 {new Date(rem.ticketSyncedAt).toLocaleString()}</span>}
                        {rem.lastVerifiedAt && <span>🧪 {new Date(rem.lastVerifiedAt).toLocaleString()}</span>}
                        {rem.exceptionReason && <span title={rem.exceptionReason}>📝 Risk: {rem.exceptionReason.slice(0, 50)}{rem.exceptionReason.length > 50 ? '…' : ''}</span>}
                        {rem.suppressionReason && <span title={rem.suppressionReason}>🫥 Suppressed: {rem.suppressionReason.slice(0, 50)}{rem.suppressionReason.length > 50 ? '…' : ''}</span>}
                        {rem.notes && <span title={rem.notes}>💬 {rem.notes.slice(0, 60)}{rem.notes.length > 60 ? '…' : ''}</span>}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    {jiraIntegration.enabled && rem && !isEditing && (
                      <>
                        {!rem.ticketRef ? (
                          <button
                            className="ce-btn ce-btn--secondary ce-btn--sm"
                            onClick={() => runJiraAction(v, 'create')}
                            disabled={ticketLoading[vulnId]}
                            title={`Create ${jiraIntegration.issueType || 'Jira'} issue in ${jiraIntegration.projectKey || 'configured project'}`}
                          >
                            {ticketLoading[vulnId] === 'create' ? '…' : t('remediation.createJira', '🎫 Create Jira')}
                          </button>
                        ) : (
                          <button
                            className="ce-btn ce-btn--secondary ce-btn--sm"
                            onClick={() => runJiraAction(v, 'sync')}
                            disabled={ticketLoading[vulnId]}
                            title="Refresh Jira ticket status"
                          >
                            {ticketLoading[vulnId] === 'sync' ? '…' : t('remediation.syncJira', '🔄 Sync Jira')}
                          </button>
                        )}
                      </>
                    )}
                    {githubIntegration.enabled && rem && !isEditing && (
                      <>
                        {(!rem.ticketRef || rem.ticketType === 'github') && (
                          !rem.ticketRef ? (
                            <button
                              className="ce-btn ce-btn--secondary ce-btn--sm"
                              onClick={() => runGitHubAction(v, 'create')}
                              disabled={githubTicketLoading[vulnId]}
                              title={`Create GitHub Issue in ${githubIntegration.defaultOwner}/${githubIntegration.defaultRepo}`}
                            >
                              {githubTicketLoading[vulnId] === 'create' ? '…' : '🐙 Create GitHub Issue'}
                            </button>
                          ) : (
                            <button
                              className="ce-btn ce-btn--secondary ce-btn--sm"
                              onClick={() => runGitHubAction(v, 'sync')}
                              disabled={githubTicketLoading[vulnId]}
                              title="Refresh GitHub Issue status"
                            >
                              {githubTicketLoading[vulnId] === 'sync' ? '…' : '🔄 Sync GitHub'}
                            </button>
                          )
                        )}
                      </>
                    )}
                    <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => (isEditing ? setEditing(null) : startEdit(v))}>
                      {isEditing ? '✕' : (rem ? '✏️' : t('remediation.track', '+ Track'))}
                    </button>
                    {rem && !isEditing && (
                      <button className="ce-btn ce-btn--danger ce-btn--sm" onClick={() => remove(vulnId)}>🗑️</button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div style={{
                    marginTop: '0.875rem',
                    paddingTop: '0.875rem',
                    borderTop: '1px solid var(--bs-border-color)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.75rem',
                  }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.statusLabel', 'Status *')}</label>
                      <select className="form-select form-select-sm" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.priorityLabel', 'Priority')}</label>
                      <select className="form-select form-select-sm" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                        <option value="">{t('remediation.inheritFromCve', '— inherit from CVE —')}</option>
                        {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.ownerLabel', 'Owner')}</label>
                      <input className="form-control form-control-sm" placeholder={t('remediation.ownerPlaceholder', 'e.g. @username or team')} value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.dueDateLabel', 'Due Date')}</label>
                      <input type="date" className="form-control form-control-sm" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.verificationLabel', 'Verification')}</label>
                      <select className="form-select form-select-sm" value={form.verificationStatus} onChange={e => setForm(f => ({ ...f, verificationStatus: e.target.value }))}>
                        {VERIFICATION_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.ticketLabel', 'Ticket / Reference')}</label>
                      <input className="form-control form-control-sm" placeholder={t('remediation.ticketPlaceholder', 'e.g. JIRA-123')} value={form.ticketRef} onChange={e => setForm(f => ({ ...f, ticketRef: e.target.value }))} />
                    </div>
                    {form.status === 'Accepted Risk' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.exceptionReasonLabel', 'Exception Reason *')}</label>
                        <textarea className="form-control form-control-sm" rows={2} placeholder={t('remediation.exceptionReasonPlaceholder', 'Why is the risk being accepted?')} value={form.exceptionReason} onChange={e => setForm(f => ({ ...f, exceptionReason: e.target.value }))} />
                      </div>
                    )}
                    {form.status === 'Suppressed' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.suppressionReasonLabel', 'Suppression Reason *')}</label>
                        <textarea className="form-control form-control-sm" rows={2} placeholder={t('remediation.suppressionReasonPlaceholder', 'Why should this finding be suppressed?')} value={form.suppressionReason} onChange={e => setForm(f => ({ ...f, suppressionReason: e.target.value }))} />
                      </div>
                    )}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.verificationNotesLabel', 'Verification Notes')}</label>
                      <textarea className="form-control form-control-sm" rows={2} placeholder={t('remediation.verificationNotesPlaceholder', 'Evidence, validation steps, or rollout notes…')} value={form.verificationNotes} onChange={e => setForm(f => ({ ...f, verificationNotes: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>{t('remediation.notesLabel', 'Notes')}</label>
                      <textarea className="form-control form-control-sm" rows={2} placeholder={t('remediation.notesPlaceholder', 'Remediation notes…')} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                    {editError && (
                      <div className="alert alert-danger py-2 mb-0" style={{ gridColumn: '1 / -1' }}>{editError}</div>
                    )}
                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem' }}>
                      <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={save} disabled={saving}>
                        {saving ? '…' : t('remediation.save', '💾 Save')}
                      </button>
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => setEditing(null)}>{t('remediation.cancel', 'Cancel')}</button>
                    </div>
                    {rem && (
                      <div style={{ gridColumn: '1 / -1', marginTop: '0.25rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.4rem' }}>{t('remediation.history', 'History')}</div>
                        {historyLoading[vulnId] ? (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)' }}>{t('remediation.loadingHistory', 'Loading history…')}</div>
                        ) : history.length === 0 ? (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)' }}>{t('remediation.noHistory', 'No history yet.')}</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                            {history.slice(0, 4).map(entry => (
                              <div key={entry.id} style={{ border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '0.6rem 0.75rem', background: 'var(--bg-secondary, rgba(148,163,184,.06))' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                  <strong style={{ fontSize: '0.78rem' }}>{entry.action}</strong>
                                  <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>{new Date(entry.createdAt).toLocaleString()}</span>
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)' }}>{entry.actorEmail || 'system'}</div>
                                {entry.changeSummary && (
                                  <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>{entry.changeSummary}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nPages > 1 && (
        <div className="mt-3">
          <Pagination
            nPages={nPages}
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageSize}
            totalItems={filtered.length}
          />
        </div>
      )}
    </div>
  );
}
